import { z } from "zod";
export const id = z.string().min(1).max(128);
export const finite = z.number().finite();
export const count = finite.int().min(0).max(Number.MAX_SAFE_INTEGER);
export const hash = z.string().regex(/^[0-9a-f]{64}$/);
export const projectIdSchema = z.string().regex(/^[a-z][a-z0-9-]{0,63}$/);
export const gameVersionSchema = z
  .string()
  .regex(/^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/)
  .refine((v) => v !== "latest");
export type JsonValue =
  | null
  | boolean
  | number
  | string
  | JsonValue[]
  | { [key: string]: JsonValue };
export type JsonObject = { [key: string]: JsonValue };
function validJson(value: unknown, depth = 1): boolean {
  if (depth > 8) return false;
  if (value === null || typeof value === "boolean") return true;
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value === "string") return value.length <= 2048;
  if (Array.isArray(value))
    return value.length <= 256 && value.every((v) => validJson(v, depth + 1));
  if (
    typeof value !== "object" ||
    Object.getPrototypeOf(value) !== Object.prototype
  )
    return false;
  const entries = Object.entries(value);
  return (
    entries.length <= 64 &&
    entries.every(
      ([k, v]) =>
        k.length <= 128 &&
        !["__proto__", "constructor", "prototype"].includes(k) &&
        validJson(v, depth + 1),
    )
  );
}
export const jsonObjectSchema = z.custom<JsonObject>((v) => {
  try {
    return (
      v !== null &&
      typeof v === "object" &&
      !Array.isArray(v) &&
      validJson(v) &&
      new TextEncoder().encode(JSON.stringify(v)).length <= 16384
    );
  } catch {
    return false;
  }
}, "Invalid or oversized JSON object");
const color = z.string().regex(/^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/);
const dimension = finite.positive().max(32768);
const coordinate = finite.min(-1e6).max(1e6);
const scale = finite
  .min(-100)
  .max(100)
  .refine((v) => v !== 0);
const common = {
  id,
  entityId: id.optional(),
  x: coordinate,
  y: coordinate,
  rotation: finite
    .min(-100 * Math.PI)
    .max(100 * Math.PI)
    .optional(),
  scaleX: scale.optional(),
  scaleY: scale.optional(),
  opacity: finite.min(0).max(1).optional(),
};
const stroke = z.strictObject({ color, width: dimension });
export const nodeSchema = z.discriminatedUnion("kind", [
  z
    .strictObject({
      ...common,
      kind: z.literal("rect"),
      width: dimension,
      height: dimension,
      fill: color,
      cornerRadius: finite.nonnegative().optional(),
      stroke: stroke.optional(),
    })
    .refine((n) => (n.cornerRadius ?? 0) <= Math.min(n.width, n.height) / 2),
  z.strictObject({
    ...common,
    kind: z.literal("circle"),
    radius: dimension,
    fill: color,
    stroke: stroke.optional(),
  }),
  z.strictObject({
    ...common,
    kind: z.literal("sprite"),
    assetId: id,
    width: dimension,
    height: dimension,
  }),
  z.strictObject({
    ...common,
    kind: z.literal("text"),
    text: z
      .string()
      .max(512)
      .refine((t) => t.split("\n").length <= 8),
    fontSize: dimension,
    color,
    align: z.enum(["left", "center", "right"]).optional(),
    weight: z.enum(["normal", "bold"]).optional(),
  }),
]);
export const snapshotDataSchema = z
  .strictObject({
    reason: z.enum(["start", "periodic", "keyframe"]),
    scene: z.strictObject({
      width: dimension,
      height: dimension,
      background: color,
    }),
    entities: z
      .array(
        z.strictObject({
          id,
          label: id,
          type: id.optional(),
          properties: jsonObjectSchema,
        }),
      )
      .max(512),
    nodes: z.array(nodeSchema).max(2048),
    metadata: jsonObjectSchema,
  })
  .superRefine((s, c) => {
    const entities = new Set(s.entities.map((e) => e.id));
    if (
      entities.size !== s.entities.length ||
      new Set(s.nodes.map((n) => n.id)).size !== s.nodes.length
    )
      c.addIssue({ code: "custom", message: "Duplicate scene identity" });
    for (const n of s.nodes)
      if (n.entityId && !entities.has(n.entityId))
        c.addIssue({ code: "custom", message: "Missing scene entity" });
  });
export type SceneNode = z.infer<typeof nodeSchema>;
export type SnapshotData = z.infer<typeof snapshotDataSchema>;
